import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Smartphone, ShieldAlert, X } from "lucide-react";
import api from "@/lib/api";

// Slim app-wide nudge shown to sellers & customers when the Mobile Number
// Verification feature is ON and they haven't verified yet. Never shown to admins.
export function MobileVerifyBanner() {
  const [req, setReq] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("mv-banner-dismissed") === "1"; } catch { return false; }
  });
  const location = useLocation();
  const navigate = useNavigate();

  const load = () => api.get("/auth/mobile/requirement").then((r) => setReq(r.data)).catch(() => {});
  useEffect(() => { load(); }, [location.pathname]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener("account-updated", onFocus);
    return () => { window.removeEventListener("focus", onFocus); window.removeEventListener("account-updated", onFocus); };
  }, []);

  if (!req || !req.applies || req.verified) return null;
  const soft = req.strictness === "prompt";
  if (soft && dismissed) return null;

  const role = req.audience; // not the deciding factor for copy; use blocking scope
  const blockedThing = "checkout and other key actions";
  const dismiss = () => { try { sessionStorage.setItem("mv-banner-dismissed", "1"); } catch {} setDismissed(true); };

  return (
    <div
      data-testid="mobile-verify-banner"
      className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5 text-sm md:px-8 ${soft ? "bg-blue-50 text-blue-800 border-b border-blue-100" : "bg-amber-50 text-amber-900 border-b border-amber-200"}`}
    >
      {soft ? <Smartphone className="h-4 w-4 shrink-0 text-blue-600" /> : <ShieldAlert className="h-4 w-4 shrink-0 text-amber-600" />}
      <span className="min-w-0 flex-1 font-medium">
        {soft
          ? "Verify your mobile number to secure your account and speed up checkout."
          : `Mobile verification required — ${blockedThing} stay locked until you verify your mobile number.`}
      </span>
      <button
        onClick={() => navigate("/verify-mobile")}
        data-testid="mobile-verify-banner-cta"
        className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold text-white ${soft ? "bg-blue-600 hover:bg-blue-700" : "bg-amber-600 hover:bg-amber-700"}`}
      >
        Verify now
      </button>
      {soft && (
        <button onClick={dismiss} data-testid="mobile-verify-banner-dismiss" title="Dismiss" className="shrink-0 rounded-full p-1 text-blue-500 hover:bg-blue-100">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
